import { Group } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import type { ComponentData } from '../../../api/pages-client/pages-client.type';
import type { TransformedTarget } from '../../../api/targets-client/targets-client.type';
import { TargetComparison } from '../../../api/targets-client/targets-client.type';
import { newCOLORS } from '../../../styles/colors';
import { skyLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { getTargetComparisonSymbol } from '../../adherence/targets/targets.helpers';
import { TARGET_THRESHOLD_COLORS, TargetThreshold } from '../../adherence/targets/targets.type';
import { type ChartType } from '../dashboards.type';
import type { SprintData, WidgetDataPoint } from '../hooks/dashboards.hooks.type';
import { BurnChart } from './burn-chart';
import { CumulativeFlowChart } from './cumulative-flow-chart';
import { ThresholdAwareLineDot } from './threshold-aware-line-dot';
import {
  addTrendLinesToData,
  getDefaultObjectColor,
  getProratedAverageTotals,
  getTargetAreaChart,
  getTargetThresholdMessage,
  getYAxisLabel,
} from './widget.helpers';

interface ChartWidgetProps {
  type: ChartType;
  data: WidgetDataPoint[];
  selectedTarget: TransformedTarget | null;
  componentData: ComponentData;
  targetThresholds: Record<string, TargetThreshold | null>;
  selectedSprint?: SprintData;
  selectedMetric?: 'tasks' | 'points';
  showTrendLine?: boolean;
  measureUnits?: string[];
}

const DEFAULT_TICK_LINE_CHARS = 12;
const STACKED_TICK_FONT_SIZE = 10;
const STACKED_TICK_LINE_HEIGHT = 12;
const STACKED_TICK_TOP_OFFSET = 14;
// Responsive X-axis label wrapping for sprint widgets: the per-line character budget is
// derived from the measured column width (see the component body), so wide screens use
// long lines (few rows) and narrow ones wrap more — instead of a fixed budget.
const X_AXIS_CHAR_PX = 6; // ≈ px per character at STACKED_TICK_FONT_SIZE
const X_AXIS_PLOT_INSET = 80; // px reserved for the Y-axis + horizontal chart margins
const X_AXIS_MIN_CHARS = 10;
const X_AXIS_MAX_CHARS = 44;

/**
 * Greedily word-wraps a single text segment to a character budget, hard-breaking any
 * individual word that is itself wider than the budget so it can never overflow.
 *
 * @param {string} segment - Text with no preferred break points beyond whitespace.
 * @param {number} maxChars - Maximum characters per line.
 * @returns {string[]} One entry per wrapped line (never empty).
 */
const wrapWords = (segment: string, maxChars: number): string[] => {
  const lines: string[] = [];
  let current = '';
  for (const word of segment.split(/\s+/).filter(Boolean)) {
    let token = word;
    while (token.length > maxChars) {
      if (current) {
        lines.push(current);
        current = '';
      }
      lines.push(token.slice(0, maxChars));
      token = token.slice(maxChars);
    }
    const candidate = current ? `${current} ${token}` : token;
    if (current && candidate.length > maxChars) {
      lines.push(current);
      current = token;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [''];
};

/**
 * Splits a work-period name into stacked lines that each fit within maxChars, using only
 * as many lines as the column width requires.
 *
 * Responsibilities:
 * 1. Return the whole name on one line when it already fits (wide columns).
 * 2. Otherwise pack consecutive " - " segments onto a line while they fit (SAFe names).
 * 3. Word-wrap (and hard-break) any segment that is itself wider than the budget.
 *
 * @param {string} value - The work-period name used as the X-axis category.
 * @param {number} maxChars - Maximum characters per line (defaults to DEFAULT_TICK_LINE_CHARS).
 * @returns {string[]} One entry per rendered line, in order (never empty).
 */
const splitSprintLabel = (value: string, maxChars: number = DEFAULT_TICK_LINE_CHARS): string[] => {
  const raw = String(value ?? '').trim();
  if (!raw) return [''];
  if (raw.length <= maxChars) return [raw];

  const segments = raw.includes(' - ') ? raw.split(' - ') : [raw];
  const lines: string[] = [];
  let current = '';

  for (const segment of segments) {
    const candidate = current ? `${current} - ${segment}` : segment;
    if (candidate.length <= maxChars) {
      current = candidate;
      continue;
    }
    if (current) {
      lines.push(current);
      current = '';
    }
    if (segment.length <= maxChars) {
      current = segment;
    } else {
      lines.push(...wrapWords(segment, maxChars));
    }
  }
  if (current) lines.push(current);

  return lines.length ? lines : [''];
};

/**
 * Custom Recharts X-axis tick that renders a long work-period name as horizontal lines
 * stacked under the tick — avoids both Recharts' overlap-dropping and angled labels.
 * The line count adapts to maxChars, which the axis derives from the column width.
 *
 * @param {object} props - Tick props injected by Recharts (position, payload, fill) plus maxChars.
 * @returns {ReactElement} A centered, multi-line SVG tick label.
 */
const StackedSprintTick = ({
  x = 0,
  y = 0,
  payload,
  fill = '#666',
  maxChars = DEFAULT_TICK_LINE_CHARS,
}: {
  x?: number;
  y?: number;
  payload?: { value?: string };
  fill?: string;
  maxChars?: number;
}) => {
  const lines = splitSprintLabel(payload?.value ?? '', maxChars);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill={fill} fontSize={STACKED_TICK_FONT_SIZE}>
        {lines.map((line, index) => (
          <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? STACKED_TICK_TOP_OFFSET : STACKED_TICK_LINE_HEIGHT}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const CustomTooltip = (props: any) => {
  const { t } = useTranslation();
  const { active, payload, label, selectedTarget, targetThresholds, isWorkPeriodWidget } = props;
  if (active && payload && payload.length) {
    // Filter out the line data, removes the helper lines for threshold dots
    const filteredPayload = payload.filter((pld: any) => pld.name !== 'reference-dot');

    const threshold = targetThresholds[label as string];
    const thresholdColor = threshold ? TARGET_THRESHOLD_COLORS[threshold as TargetThreshold] : undefined;

    return (
      <div
        style={{
          backgroundColor: 'white',
          padding: '8px 12px',
          border: '1px solid #e9ecef',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
        }}
      >
        <p style={{ margin: '0 0 5px', fontWeight: 'bold' }}>{label}</p>
        {filteredPayload.map((pld: any, index: number) => {
          const isTarget = pld.dataKey && pld.dataKey.startsWith('target_');
          const isTrend = pld.dataKey && pld.dataKey.endsWith('_trend');
          const displayName = isTarget ? pld.name : pld.payload.objectName || pld.name;
          return (
            <p key={index} style={{ margin: '0', color: pld.color }}>
              {`${displayName}${isTrend && !displayName.endsWith(' (Trend)') ? ' (Trend)' : ''}`}:{' '}
              {pld.value != null ? pld.value.toLocaleString() : 'N/A'}
            </p>
          );
        })}
        {threshold && selectedTarget && (
          <Group gap={5}>
            <Icon name="circle" variant="filled" color={thresholdColor} size={10} />
            <p style={{ margin: '3px 0 0' }}>
              {getTargetThresholdMessage(
                isWorkPeriodWidget
                  ? {
                      green_threshold_lower: selectedTarget.green_threshold_lower,
                      green_threshold_upper: selectedTarget.green_threshold_upper,
                      yellow_threshold_lower: selectedTarget.yellow_threshold_lower,
                      yellow_threshold_upper: selectedTarget.yellow_threshold_upper,
                      red_threshold_lower: selectedTarget.red_threshold_lower,
                      red_threshold_upper: selectedTarget.red_threshold_upper,
                    }
                  : selectedTarget.thresholds[label],
                threshold,
                t,
              )}
            </p>
          </Group>
        )}
      </div>
    );
  }
  return null;
};

export const ChartWidget: FC<ChartWidgetProps> = ({
  type,
  data,
  selectedTarget,
  componentData,
  targetThresholds,
  selectedSprint,
  selectedMetric = 'points',
  showTrendLine = false,
  measureUnits,
}) => {
  const { t } = useTranslation();
  const { ref: chartContainerRef, width: chartContainerWidth } = useElementSize();
  const colorMap = componentData?.color_map || {};
  const isWorkPeriodWidget = componentData.object_type === 'sprint';
  const measureColors = componentData?.measure_colors; // Comma-separated colors for measures

  // Check if we have multiple measures
  const measures = componentData.measure ? componentData.measure.split(',').filter((m) => m.trim()) : [];
  const hasMultipleMeasures = measures.length > 1;

  // Group data by objectId for multiple lines
  // When we have multiple measures, we want to group by measure instead
  const objectIds = hasMultipleMeasures
    ? Array.from(new Set(data.map((item) => item.measure).filter((m): m is string => !!m)))
    : Array.from(new Set(data.map((item) => item.objectId)));

  // Consolidate data points by date
  const consolidatedData = data.reduce(
    (acc, curr) => {
      // Skip data points without required fields
      if (!curr.date) return acc;

      const existingPoint = acc.find((point) => point.date === curr.date);
      // Use measure as the key if we have multiple measures, otherwise use objectId
      const dataKey = hasMultipleMeasures ? curr.measure : curr.objectId;

      // Skip if dataKey is undefined
      if (!dataKey) return acc;

      if (existingPoint) {
        existingPoint[dataKey] = curr.value;
        existingPoint[`${dataKey}Name`] = curr.objectName;
      } else {
        const newPoint: { date: string; [key: string]: number | string } = {
          date: curr.date,
          [dataKey]: curr.value,
          [`${dataKey}Name`]: curr.objectName,
        };
        acc.push(newPoint);
      }
      return acc;
    },
    [] as { date: string; [key: string]: number | string | number[] }[],
  );

  // Sort by date to ensure proper ordering
  consolidatedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const getChartData = () => {
    // For multiple measures, always use consolidatedData to properly group by date
    // For single measure work period widgets, use raw data
    let chartData =
      isWorkPeriodWidget && !hasMultipleMeasures && (type === 'line' || type === 'area')
        ? data.map((d) => ({ ...d }))
        : consolidatedData;
    if (selectedTarget) {
      const targetId = selectedTarget.id;
      const targetKey = `target_${targetId}`;
      const isRangeTarget =
        selectedTarget.target_comparison === TargetComparison.RANGE_IN ||
        selectedTarget.target_comparison === TargetComparison.RANGE_EX;

      // Add target values to each data point
      chartData.forEach((dataPoint) => {
        if (selectedTarget.is_work_period_target) {
          if (isRangeTarget && selectedTarget.target_value_upper) {
            dataPoint[targetKey] = [selectedTarget.target_value, selectedTarget.target_value_upper];
          } else {
            dataPoint[targetKey] = selectedTarget.target_value;
          }
        } else {
          const date = dataPoint.date;

          const timeAllocation = selectedTarget.time_allocations[date];
          if (timeAllocation && isRangeTarget && typeof timeAllocation === 'object') {
            dataPoint[targetKey] = [timeAllocation.value_lower, timeAllocation.value_upper];
          } else if (timeAllocation && typeof timeAllocation === 'number') {
            dataPoint[targetKey] = timeAllocation;
          }
        }
      });
    }

    // Add trend lines if enabled
    if (showTrendLine && chartData.length > 1) {
      chartData = addTrendLinesToData(chartData, isWorkPeriodWidget) as typeof chartData;
    }

    return chartData;
  };

  const getPieData = () => {
    if (data.length === 0) {
      return [];
    }
    let comparison: string | null = null;
    if (selectedTarget) {
      const { total_lower, total_upper } = getProratedAverageTotals(selectedTarget);
      comparison = getTargetComparisonSymbol(selectedTarget.target_comparison, total_lower, total_upper ?? null);
    }
    const pieData = objectIds.map((objectId) => {
      const objectData = data.filter((d) => d.objectId === objectId);
      if (!objectData.length) {
        return {
          id: objectId,
          name: objectData[0]?.objectName || objectId,
          value: 0,
          comparison: comparison,
        };
      }
      const total = objectData.reduce((sum, item) => sum + item.value, 0);
      return {
        id: objectId,
        name: objectData[0]?.objectName || objectId,
        value: total,
        comparison: comparison,
      };
    });
    return pieData;
  };

  const yAxisLabelText = measureUnits ? getYAxisLabel(measureUnits) : '';

  const Y_AXIS_LABEL_LEFT_MARGIN = 15;
  const DEFAULT_LEFT_MARGIN = 0;

  // Work-period (sprint) widgets label the X-axis with full work-period names
  // (e.g. "FY26 - PI 1 - Iteration 3 - Dec 2025"). These are long, so Recharts' default
  // interval="preserveEnd" silently drops every label that would overlap a neighbor —
  // showing ~3 of 6. Render EVERY label (interval={0}) as horizontal lines stacked under
  // its point via a custom tick. The per-line character budget is derived from the
  // measured column width, so wide screens use long lines and narrow ones wrap more.
  const isSprintAxis = isWorkPeriodWidget;
  const xCategoryCount = Math.max(1, new Set(data.map((point) => point.date)).size);
  const xBandWidth =
    isSprintAxis && chartContainerWidth > 0 ? Math.max(0, chartContainerWidth - X_AXIS_PLOT_INSET) / xCategoryCount : 0;
  const sprintLabelMaxChars = xBandWidth
    ? Math.max(X_AXIS_MIN_CHARS, Math.min(X_AXIS_MAX_CHARS, Math.floor(xBandWidth / X_AXIS_CHAR_PX)))
    : DEFAULT_TICK_LINE_CHARS;
  const maxLabelLines = isSprintAxis
    ? data.reduce(
        (max, point) => Math.max(max, splitSprintLabel(String(point.date ?? ''), sprintLabelMaxChars).length),
        1,
      )
    : 1;
  const stackedXAxisHeight = Math.min(120, STACKED_TICK_TOP_OFFSET + maxLabelLines * STACKED_TICK_LINE_HEIGHT + 6);

  const commonProps = {
    width: 500,
    height: 300,
    margin: { top: 10, right: 30, left: yAxisLabelText ? Y_AXIS_LABEL_LEFT_MARGIN : DEFAULT_LEFT_MARGIN, bottom: 5 },
  };

  const xAxisComponent = (
    <XAxis
      key="x-axis"
      dataKey="date"
      axisLine={{ stroke: skyLight }}
      tickLine={{ stroke: skyLight }}
      tick={isSprintAxis ? <StackedSprintTick maxChars={sprintLabelMaxChars} /> : { fontSize: 12 }}
      interval={isSprintAxis ? 0 : undefined}
      height={isSprintAxis ? stackedXAxisHeight : undefined}
    />
  );
  const yAxisComponent = (
    <YAxis
      key="y-axis"
      tick={{ fontSize: 12 }}
      axisLine={{ stroke: skyLight }}
      tickLine={{ stroke: skyLight }}
      {...(yAxisLabelText
        ? {
            label: {
              value: yAxisLabelText,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: 11 },
              offset: 10,
            },
          }
        : {})}
    />
  );
  const cartesianGridComponent = <CartesianGrid key="grid" stroke={skyLight} strokeDasharray="3 3" />;
  const commonComponents = [xAxisComponent, yAxisComponent, cartesianGridComponent];
  const renderChart = (chartType: ChartType) => {
    // Handle specialized sprint charts
    if (chartType === 'burn_up' || chartType === 'burn_down') {
      return <BurnChart data={selectedSprint} chartType={chartType} selectedMetric={selectedMetric} />;
    }

    if (chartType === 'cumulative_flow') {
      return (
        <CumulativeFlowChart data={selectedSprint} selectedMetric={selectedMetric} phases={selectedSprint?.phases} />
      );
    }

    switch (chartType) {
      case 'line':
        return (
          <AreaChart {...commonProps} data={getChartData()}>
            {commonComponents}
            <RechartsTooltip
              content={
                <CustomTooltip
                  selectedTarget={selectedTarget}
                  targetThresholds={targetThresholds}
                  isWorkPeriodWidget={isWorkPeriodWidget}
                />
              }
            />
            {hasMultipleMeasures && <Legend />}
            {isWorkPeriodWidget && !hasMultipleMeasures ? (
              <Area
                key="work-period-area"
                type="monotone"
                dataKey="value"
                stroke={getDefaultObjectColor(data[0]?.parentId || '', colorMap)}
                strokeWidth={2}
                fill="transparent"
                connectNulls={true}
                dot={
                  <ThresholdAwareLineDot
                    color={getDefaultObjectColor(data[0]?.parentId || '', colorMap)}
                    targetThresholds={targetThresholds}
                  />
                }
              />
            ) : (
              objectIds.map((objectId, index) => {
                const color = getDefaultObjectColor(
                  objectId,
                  colorMap,
                  index,
                  hasMultipleMeasures ? measureColors : undefined,
                );
                // Find the display name - when multiple measures, objectId is the measure name
                const displayName = hasMultipleMeasures
                  ? data.find((d) => d.measure === objectId)?.objectName || objectId
                  : data.find((d) => d.objectId === objectId)?.objectName || objectId;
                return (
                  <Area
                    key={objectId}
                    type="monotone"
                    dataKey={objectId}
                    stroke={color}
                    strokeWidth={2}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    name={displayName}
                    fill="transparent"
                    connectNulls={true}
                    dot={<ThresholdAwareLineDot color={color} targetThresholds={targetThresholds} />}
                  />
                );
              })
            )}
            {selectedTarget && getTargetAreaChart(selectedTarget, t)}
            {showTrendLine && isWorkPeriodWidget && !hasMultipleMeasures && (
              <Line
                key="work-period-trend"
                type="monotone"
                dataKey="value_trend"
                stroke={getDefaultObjectColor(data[0]?.parentId || '', colorMap)}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name={`${data[0]?.objectName || 'Value'} (Trend)`}
                legendType="none"
              />
            )}
            {showTrendLine &&
              !(isWorkPeriodWidget && !hasMultipleMeasures) &&
              objectIds.map((objectId, index) => {
                const trendKey = `${objectId}_trend`;
                const color = getDefaultObjectColor(
                  objectId,
                  colorMap,
                  index,
                  hasMultipleMeasures ? measureColors : undefined,
                );
                const displayName = hasMultipleMeasures
                  ? data.find((d) => d.measure === objectId)?.objectName || objectId
                  : data.find((d) => d.objectId === objectId)?.objectName || objectId;
                return (
                  <Line
                    key={`${objectId}-trend`}
                    type="monotone"
                    dataKey={trendKey}
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name={`${displayName} (Trend)`}
                    legendType="none"
                  />
                );
              })}
          </AreaChart>
        );
      case 'area':
        return (
          <AreaChart {...commonProps} data={getChartData()}>
            {hasMultipleMeasures && <Legend />}
            {isWorkPeriodWidget && !hasMultipleMeasures ? (
              <defs key={`gradient-${componentData.id}`}>
                <linearGradient id={`colorValue-${componentData.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={getDefaultObjectColor(data[0]?.parentId || '', colorMap)}
                    stopOpacity={0.1}
                  />
                  <stop
                    offset="95%"
                    stopColor={getDefaultObjectColor(data[0]?.parentId || '', colorMap)}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
            ) : (
              objectIds.map((objectId, index) => {
                const color = getDefaultObjectColor(
                  objectId,
                  colorMap,
                  index,
                  hasMultipleMeasures ? measureColors : undefined,
                );
                return (
                  <defs key={`gradient-${objectId}`}>
                    <linearGradient id={`colorValue-${objectId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color} stopOpacity={0.1} />
                      <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                );
              })
            )}
            {commonComponents}
            <RechartsTooltip
              content={
                <CustomTooltip
                  selectedTarget={selectedTarget}
                  targetThresholds={targetThresholds}
                  isWorkPeriodWidget={isWorkPeriodWidget}
                />
              }
            />
            {isWorkPeriodWidget && !hasMultipleMeasures ? (
              <Area
                key={`gradient-${componentData.id}`}
                type="monotone"
                dataKey="value"
                stroke={getDefaultObjectColor(data[0]?.parentId || '', colorMap)}
                strokeWidth={2}
                fill={`url(#colorValue-${componentData.id})`}
                fillOpacity={1}
                connectNulls={true}
                dot={
                  <ThresholdAwareLineDot
                    color={getDefaultObjectColor(data[0]?.parentId || '', colorMap)}
                    targetThresholds={targetThresholds}
                  />
                }
              />
            ) : (
              objectIds.map((objectId, index) => {
                const color = getDefaultObjectColor(
                  objectId,
                  colorMap,
                  index,
                  hasMultipleMeasures ? measureColors : undefined,
                );
                const displayName = hasMultipleMeasures
                  ? data.find((d) => d.measure === objectId)?.objectName || objectId
                  : data.find((d) => d.objectId === objectId)?.objectName || objectId;
                return (
                  <Area
                    key={`gradient-${objectId}`}
                    type="monotone"
                    dataKey={objectId}
                    stroke={color}
                    strokeWidth={2}
                    fill={`url(#colorValue-${objectId})`}
                    fillOpacity={1}
                    name={displayName}
                    connectNulls={true}
                    dot={<ThresholdAwareLineDot color={color} targetThresholds={targetThresholds} />}
                  />
                );
              })
            )}
            {selectedTarget && getTargetAreaChart(selectedTarget, t)}
            {showTrendLine && isWorkPeriodWidget && !hasMultipleMeasures && (
              <Line
                key="work-period-trend"
                type="monotone"
                dataKey="value_trend"
                stroke={getDefaultObjectColor(data[0]?.parentId || '', colorMap)}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name={`${data[0]?.objectName || 'Value'} (Trend)`}
                legendType="none"
              />
            )}
            {showTrendLine &&
              !(isWorkPeriodWidget && !hasMultipleMeasures) &&
              objectIds.map((objectId, index) => {
                const trendKey = `${objectId}_trend`;
                const color = getDefaultObjectColor(
                  objectId,
                  colorMap,
                  index,
                  hasMultipleMeasures ? measureColors : undefined,
                );
                const displayName = hasMultipleMeasures
                  ? data.find((d) => d.measure === objectId)?.objectName || objectId
                  : data.find((d) => d.objectId === objectId)?.objectName || objectId;
                return (
                  <Line
                    key={`${objectId}-trend`}
                    type="monotone"
                    dataKey={trendKey}
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name={`${displayName} (Trend)`}
                    legendType="none"
                  />
                );
              })}
          </AreaChart>
        );
      case 'bar': {
        const barChartData = getChartData();
        const hasUsableThreshold = Object.values(targetThresholds).some((threshold) => threshold != null);
        const shouldUseColoredBars = Boolean(selectedTarget && hasUsableThreshold);
        return (
          <ComposedChart {...commonProps} data={barChartData}>
            {commonComponents}
            <RechartsTooltip
              content={
                <CustomTooltip
                  selectedTarget={selectedTarget}
                  targetThresholds={targetThresholds}
                  isWorkPeriodWidget={isWorkPeriodWidget}
                />
              }
            />
            {hasMultipleMeasures && <Legend />}
            {objectIds.map((objectId, index) => {
              const color =
                isWorkPeriodWidget && !hasMultipleMeasures
                  ? getDefaultObjectColor(data[0]?.parentId || '', colorMap)
                  : getDefaultObjectColor(objectId, colorMap, index, hasMultipleMeasures ? measureColors : undefined);
              const displayName = hasMultipleMeasures
                ? data.find((d) => d.measure === objectId)?.objectName || objectId
                : data.find((d) => d.objectId === objectId)?.objectName || objectId;
              return (
                <Fragment key={objectId}>
                  <Bar dataKey={objectId} fill={color} radius={[4, 4, 0, 0]} name={displayName}>
                    {shouldUseColoredBars &&
                      barChartData.map((entry) => {
                        const threshold = targetThresholds[entry.date];
                        const barColor = threshold ? TARGET_THRESHOLD_COLORS[threshold] : color;
                        return <Cell key={entry.date} fill={barColor} />;
                      })}
                  </Bar>
                  {!shouldUseColoredBars && selectedTarget && targetThresholds && (
                    <Line
                      name="reference-dot"
                      key={`reference-dot-${objectId}`}
                      dataKey={objectId}
                      strokeWidth={0}
                      dot={<ThresholdAwareLineDot color={color} targetThresholds={targetThresholds} />}
                    />
                  )}
                </Fragment>
              );
            })}
            {showTrendLine &&
              objectIds.map((objectId, index) => {
                const trendKey = `${objectId}_trend`;
                const color =
                  isWorkPeriodWidget && !hasMultipleMeasures
                    ? getDefaultObjectColor(data[0]?.parentId || '', colorMap)
                    : getDefaultObjectColor(objectId, colorMap, index, hasMultipleMeasures ? measureColors : undefined);
                const displayName = hasMultipleMeasures
                  ? data.find((d) => d.measure === objectId)?.objectName || objectId
                  : data.find((d) => d.objectId === objectId)?.objectName || objectId;
                return (
                  <Line
                    key={`${objectId}-trend`}
                    type="monotone"
                    dataKey={trendKey}
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name={`${displayName} (Trend)`}
                    legendType="none"
                  />
                );
              })}
            {selectedTarget && getTargetAreaChart(selectedTarget, t)}
          </ComposedChart>
        );
      }
      case 'stacked_bar':
        return (
          <ComposedChart {...commonProps} data={getChartData()}>
            {commonComponents}
            <RechartsTooltip
              content={
                <CustomTooltip
                  selectedTarget={selectedTarget}
                  targetThresholds={targetThresholds}
                  isWorkPeriodWidget={isWorkPeriodWidget}
                />
              }
            />
            {objectIds.map((objectId, index) => {
              const color =
                isWorkPeriodWidget && !hasMultipleMeasures
                  ? getDefaultObjectColor(data[0]?.parentId || '', colorMap)
                  : getDefaultObjectColor(objectId, colorMap, index, hasMultipleMeasures ? measureColors : undefined);
              const displayName = hasMultipleMeasures
                ? data.find((d) => d.measure === objectId)?.objectName || objectId
                : data.find((d) => d.objectId === objectId)?.objectName || objectId;
              return (
                <Fragment key={objectId}>
                  <Bar key={objectId} dataKey={objectId} stackId="a" fill={color} name={displayName} />
                  {selectedTarget && targetThresholds && (
                    <Line
                      name="reference-dot"
                      key={`reference-dot-${objectId}`}
                      dataKey={objectId}
                      strokeWidth={0}
                      dot={<ThresholdAwareLineDot color={color} targetThresholds={targetThresholds} />}
                    />
                  )}
                </Fragment>
              );
            })}
            {showTrendLine &&
              objectIds.map((objectId, index) => {
                const trendKey = `${objectId}_trend`;
                const color =
                  isWorkPeriodWidget && !hasMultipleMeasures
                    ? getDefaultObjectColor(data[0]?.parentId || '', colorMap)
                    : getDefaultObjectColor(objectId, colorMap, index, hasMultipleMeasures ? measureColors : undefined);
                const displayName = hasMultipleMeasures
                  ? data.find((d) => d.measure === objectId)?.objectName || objectId
                  : data.find((d) => d.objectId === objectId)?.objectName || objectId;
                return (
                  <Line
                    key={`${objectId}-trend`}
                    type="monotone"
                    dataKey={trendKey}
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name={`${displayName} (Trend)`}
                    legendType="none"
                  />
                );
              })}
            {selectedTarget && getTargetAreaChart(selectedTarget, t)}
          </ComposedChart>
        );
      case 'pie':
        return (
          <PieChart {...commonProps}>
            <Pie
              data={getPieData()}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, percent }) => `${name} (${(((percent as number) || 0) * 100).toFixed(0)}%)`}
              labelLine={false}
            >
              {getPieData().map((entry: any, index: any) => {
                const color = isWorkPeriodWidget
                  ? getDefaultObjectColor(data[0]?.parentId || '', colorMap)
                  : getDefaultObjectColor(entry.id, colorMap, index);
                return <Cell key={entry.id} fill={color} />;
              })}
            </Pie>
            <RechartsTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0];
                  const value = data.value as number;
                  return (
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '8px 12px',
                        border: '1px solid #e9ecef',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      }}
                    >
                      <Text size="small" weight="medium">
                        {data.name}: {value.toLocaleString()}
                      </Text>
                      {data.payload.comparison && (
                        <Text size="tiny" color={newCOLORS.darkGray}>
                          Target: {data.payload.comparison}
                        </Text>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
          </PieChart>
        );
      default:
        return renderChart('line');
    }
  };

  // Special charts (burn/flow) already have ResponsiveContainer, don't wrap them again
  if (type === 'burn_up' || type === 'burn_down' || type === 'cumulative_flow') {
    return renderChart(type);
  }

  return (
    <div ref={chartContainerRef} style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        {renderChart(type)}
      </ResponsiveContainer>
    </div>
  );
};
